import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Loader2, AlertTriangle } from "lucide-react";
import { apiClient } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import AdminShell from "@/components/admin/AdminShell";
import { CASE_STATUS_LABELS, euros } from "@/constants/business_status";

function Kpi({ label, value, sub, testId, to }) {
  const inner = (
    <div data-testid={testId} className="bg-white rounded-2xl shadow-sm px-5 py-4 h-full">
      <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      <div className="text-2xl font-extrabold text-navy mt-1">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

function ConfigAlert({ status }) {
  if (!status) return null;
  const items = [];
  if (!status.email?.configured) {
    items.push(`E-mails non opérationnels (${(status.email?.missing || []).join(", ")}) — ${status.email?.queued || 0} message(s) en file d'attente, aucun envoi perdu`);
  } else if (status.email?.failed > 0) {
    items.push(`${status.email.failed} e-mail(s) en échec — relance possible depuis le journal`);
  }
  (status.company_missing || []).forEach((m) => items.push(`À renseigner : ${m}`));
  if (!status.maps?.configured) items.push("Calcul automatique de trajet inactif — la saisie manuelle reste disponible");
  if (!status.followups?.enabled) items.push("Relances automatiques de devis non activées (Paramètres > Relances devis)");
  if (items.length === 0) return null;
  return (
    <div data-testid="config-alert" className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 mb-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-amber-800 mb-1">
        <AlertTriangle className="w-4 h-4" /> Paramètres à finaliser (visible uniquement en interne)
      </div>
      <ul className="text-xs text-amber-700 space-y-0.5 pl-6 list-disc">
        {items.map((it, i) => <li key={i}>{it}</li>)}
      </ul>
      <Link to="/admin/parametres" className="text-xs font-semibold text-amber-800 underline mt-1 inline-block" data-testid="config-alert-link">Ouvrir les paramètres →</Link>
    </div>
  );
}

export default function DashboardPage() {
  const { admin } = useAdminAuth();
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  useEffect(() => {
    if (!admin) return;
    const params = {};
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    apiClient.get("/admin/dashboard", { params }).then((r) => setData(r.data)).catch(() => {});
  }, [admin, from, to]);

  useEffect(() => {
    if (!admin) return;
    apiClient.get("/admin/config-status").then((r) => setConfig(r.data)).catch(() => {});
  }, [admin]);

  if (admin === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-navy" /></div>;
  if (admin === false) return <Navigate to="/admin/login" replace />;
  if (!data) return <AdminShell><div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-navy" /></div></AdminShell>;

  const bs = data.cases.by_status || {};
  return (
    <AdminShell>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <h1 className="font-heading font-extrabold text-2xl text-navy flex-1">Tableau de bord</h1>
        <input data-testid="dashboard-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm bg-white" />
        <input data-testid="dashboard-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm bg-white" />
      </div>

      <ConfigAlert status={config} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi testId="kpi-cases" label="Dossiers actifs" value={data.cases.total} to="/admin" />
        <Kpi testId="kpi-tocontact" label="À contacter" value={data.cases.to_contact} to="/admin" />
        <Kpi testId="kpi-quotes-sent" label="Devis envoyés" value={data.quotes.sent} sub={`${data.quotes.draft} en préparation`} to="/admin/devis" />
        <Kpi testId="kpi-acceptance" label="Taux d'acceptation" value={`${data.quotes.acceptance_rate} %`} sub={`${data.quotes.accepted} acceptés / ${data.quotes.refused} refusés`} />
      </div>

      {data.finance && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <Kpi testId="kpi-pipeline" label="Valeur devis en cours" value={euros(data.quotes.pipeline_value_cents)} />
          <Kpi testId="kpi-accepted-value" label="Devis acceptés" value={euros(data.quotes.accepted_value_cents)} />
          <Kpi testId="kpi-invoiced" label="CA facturé" value={euros(data.finance.invoiced_cents)} to="/admin/factures" />
          <Kpi testId="kpi-outstanding" label="Reste à encaisser" value={euros(data.finance.outstanding_cents)} sub={`${euros(data.finance.payments_recorded_cents)} encaissés`} to="/admin/paiements" />
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Kpi testId="kpi-missions-today" label="Missions aujourd'hui" value={data.missions.today} to="/admin/planning" />
        <Kpi testId="kpi-missions-planned" label="Missions planifiées" value={data.missions.planned} to="/admin/missions" />
        <Kpi testId="kpi-incidents" label="Incidents ouverts" value={data.incidents_open} to="/admin/incidents" />
        <Kpi testId="kpi-tasks-late" label="Tâches en retard" value={data.tasks_late} to="/admin/taches" />
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-5">
        <h2 className="font-bold text-navy mb-3">Répartition des dossiers par statut</h2>
        <div className="flex flex-wrap gap-2">
          {Object.entries(bs).filter(([, v]) => v > 0).map(([k, v]) => (
            <span key={k} className="text-xs font-semibold bg-navy/5 text-navy rounded-full px-3 py-1.5">
              {CASE_STATUS_LABELS[k] || k} : {v}
            </span>
          ))}
        </div>
      </div>
    </AdminShell>
  );
}
